import { Timestamp } from 'firebase/firestore';

/**
 * Utilitário puro responsável pelas pesadas lógicas de cálculo de progressão,
 * nota, experiência e passagem de nível na Avaliação Técnica.
 */

export const calculateMemberEvaluation = ({
    memberId,
    previousEval,
    allTopicIds,
    topicMetaById,
    draftLevelsByTopicId,
    levels,
    baseLevel,
    maxLevelId,
    maxLevelValue,
    gamificationSettings
}) => {
    // 1. Array de Tópicos
    const allTopicsEvaluated = (allTopicIds || []).map(topicId => {
        const meta = topicMetaById[topicId] || {};
        let levelId = draftLevelsByTopicId[topicId]?.[memberId];
        let levelDoc = null;

        if (levelId) {
            levelDoc = levels.find(l => String(l.id) === String(levelId));
        } else if (baseLevel) {
            levelDoc = baseLevel;
            levelId = baseLevel.id;
        }

        if (!levelId) return null;

        return {
            id: topicId,
            name: meta.name || meta.title || "Tópico",
            isFundamental: !!meta.isFundamental,
            idLevel: levelId,
            levelName: levelDoc?.name || levelDoc?.title || "Avaliado",
            levelValue: Number(levelDoc?.value || levelDoc?.order || 0),
            achieved: levelDoc?.isPassing !== false,
            idObjective: meta.idObjective,
            objectiveName: meta.objectiveName,
            updatedAt: Timestamp.now()
        };
    }).filter(Boolean);

    // 2. Agrupamento em Objetivos
    const objectivesMap = {};
    allTopicsEvaluated.forEach(t => {
        const oId = t.idObjective || "geral";
        if (!objectivesMap[oId]) {
            objectivesMap[oId] = { id: oId, name: t.objectiveName || "Geral", topics: [] };
        }
        const { idObjective, objectiveName, ...topicData } = t;
        objectivesMap[oId].topics.push(topicData);
    });
    const objectivesToSave = Object.values(objectivesMap);

    // 3. Cálculo de XP por Tópico Imediato
    let topicXpTotal = 0;
    const topicXpDetails = [];

    if (gamificationSettings && maxLevelId) {
        for (const topicId of (allTopicIds || [])) {
            const meta = topicMetaById[topicId];
            const topicXp = Number(meta?.xp || 0);
            if (topicXp <= 0) continue;

            const levelId = draftLevelsByTopicId[topicId]?.[memberId];
            if (String(levelId) === maxLevelId) {
                let alreadyHadMaxInPast = false;
                if (previousEval?.objectives) {
                    alreadyHadMaxInPast = previousEval.objectives.some(obj => 
                        obj.topics && obj.topics.some(t => 
                            String(t.id || t.idTopic) === String(topicId) && String(t.idLevel) === maxLevelId
                        )
                    );
                }

                if (!alreadyHadMaxInPast) {
                    topicXpTotal += topicXp;
                    topicXpDetails.push({ topicId, name: meta?.title || 'Tópico', xp: topicXp });
                }
            }
        }
    }

    // 4. Passagem de Nível e Progresso
    let memberPassed = false;
    let progressPercent = 0;

    if (gamificationSettings && maxLevelValue > 0 && (allTopicIds || []).length > 0) {
        let progressSum = 0;
        for (const topicId of (allTopicIds || [])) {
            const levelId = draftLevelsByTopicId[topicId]?.[memberId];
            const levelDoc = levels.find(l => String(l.id) === String(levelId));
            progressSum += Number(levelDoc?.order || levelDoc?.value || 0);
        }
        progressPercent = Math.round((progressSum / (allTopicIds.length * maxLevelValue)) * 100);

        const minPercent = Number(gamificationSettings.minScoreToPassPercent || 70);
        const passedPercent = progressPercent >= minPercent;

        let passedFundamentals = true;
        if (gamificationSettings.requireFundamentalsMaxScore) {
            const fundamentalTopics = (allTopicIds || []).filter(tId => topicMetaById[tId]?.isFundamental);
            if (fundamentalTopics.length > 0) {
                passedFundamentals = fundamentalTopics.every(tId => {
                    const levelId = draftLevelsByTopicId[tId]?.[memberId];
                    return String(levelId) === maxLevelId;
                });
            }
        }

        memberPassed = passedPercent && passedFundamentals;
    }

    // Anti-fraude global para XP Pass (Não dá reward em reavaliações)
    let givePassReward = memberPassed;
    if (memberPassed && previousEval) {
        const hadPassedInPast = previousEval.status === 'PASSED' || previousEval.xpPassAwarded;
        if (hadPassedInPast) {
            givePassReward = false;
        }
    }

    return {
        objectivesToSave,
        topicXpTotal,
        topicXpDetails,
        memberPassed,
        progressPercent,
        givePassReward,
        isEmpty: allTopicsEvaluated.length === 0
    };
};
